const UNDER_TWENTY = [
	'zero',
	'one',
	'two',
	'three',
	'four',
	'five',
	'six',
	'seven',
	'eight',
	'nine',
	'ten',
	'eleven',
	'twelve',
	'thirteen',
	'fourteen',
	'fifteen',
	'sixteen',
	'seventeen',
	'eighteen',
	'nineteen',
];

const TENS = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];

/** Index is the thousands-group position, so groups[2] is the millions group. */
const SCALES = ['', 'thousand', 'million', 'billion', 'trillion', 'quadrillion'];

const underHundred = (value: number): string => {
	if (value < 20) {
		return UNDER_TWENTY[value] ?? '';
	}
	const tens = TENS[Math.floor(value / 10)] ?? '';
	const units = value % 10;
	return units === 0 ? tens : `${tens}-${UNDER_TWENTY[units] ?? ''}`;
};

const underThousand = (value: number): string => {
	const hundreds = Math.floor(value / 100);
	const remainder = value % 100;
	if (hundreds === 0) {
		return underHundred(remainder);
	}
	const head = `${UNDER_TWENTY[hundreds] ?? ''} hundred`;
	// British usage: the "and" sits between the hundreds and whatever follows them.
	return remainder === 0 ? head : `${head} and ${underHundred(remainder)}`;
};

const integerToWords = (value: number): string => {
	if (value === 0) {
		return 'zero';
	}
	const groups: number[] = [];
	let remaining = value;
	while (remaining > 0) {
		groups.push(remaining % 1000);
		remaining = Math.floor(remaining / 1000);
	}
	if (groups.length > SCALES.length) {
		return String(value);
	}

	const chunks: string[] = [];
	for (let position = groups.length - 1; position >= 0; position -= 1) {
		const group = groups[position] ?? 0;
		if (group === 0) {
			continue;
		}
		// "one thousand AND five", but "one thousand two hundred and fifty" — the leading
		// "and" only appears when the final group is a bare number below a hundred.
		const needsAnd = position === 0 && group < 100 && chunks.length > 0;
		const scale = SCALES[position] ?? '';
		const body = underThousand(group);
		chunks.push(`${needsAnd ? 'and ' : ''}${body}${scale === '' ? '' : ` ${scale}`}`);
	}
	return chunks.join(' ');
};

/** Liquid hands values through as strings as often as numbers, so both are accepted. */
const toNumber = (value: unknown): number => {
	if (typeof value === 'number') {
		return value;
	}
	if (typeof value === 'string' && value.trim() !== '') {
		return Number(value);
	}
	return Number.NaN;
};

/**
 * Spells a number out in British English: 1250 → "one thousand two hundred and fifty".
 *
 * Deeds state a quantity in figures and in words side by side so the two can be checked
 * against each other, which is the whole point of the convention — so this must never
 * silently round. A fractional part is read out digit by digit after "point" rather than
 * dropped, and anything that is not a finite number returns an empty string, matching how
 * every other format filter fails.
 */
export const numberToWords = (value: unknown): string => {
	const numeric = toNumber(value);
	if (!Number.isFinite(numeric)) {
		return '';
	}
	if (Math.abs(numeric) > Number.MAX_SAFE_INTEGER) {
		// Beyond 2^53 the input has already lost precision, so spelling it out would state
		// a figure the source never held.
		return String(numeric);
	}

	const sign = numeric < 0 ? 'minus ' : '';
	const absolute = Math.abs(numeric);
	const whole = Math.trunc(absolute);
	const words = integerToWords(whole);

	// Read off the decimal STRING, not a float remainder: 0.1 + 0.2 arithmetic would
	// produce "point two nine nine nine…".
	const [, fraction] = absolute.toString().split('.');
	if (fraction === undefined || fraction === '') {
		return `${sign}${words}`;
	}
	const digits = [...fraction]
		.map((digit) => UNDER_TWENTY[Number(digit)] ?? '')
		.filter((digit) => digit !== '')
		.join(' ');
	return digits === '' ? `${sign}${words}` : `${sign}${words} point ${digits}`;
};
